/**
 * Scoring what a child said.
 *
 * The recognition itself happens on the device and never leaves it
 * ([D-004](../../../docs/DECISIONS.md#d-004),
 * [D-011](../../../docs/DECISIONS.md#d-011)). What arrives here is a
 * **transcript** — text — and what leaves is a score plus a per-sound
 * confidence for the pronunciation gap map. No audio, ever, in either
 * direction.
 *
 * Three rules shape every decision in this file, and all three come from the
 * design rather than from taste:
 *
 * - **It never gates progress** ([D-009](../../../docs/DECISIONS.md#d-009)).
 *   Speaking informs what Sneakers practises next; it never stops a child.
 * - **It never reports a failure.** Recognition of a Danish nine-year-old
 *   speaking English is going to be mediocre, and it is the *recogniser* that
 *   is unreliable, not the child. So the worst outcome here is `partial`, and
 *   `incorrect` is not reachable — a structural promise rather than a rule
 *   somebody has to remember at the call site.
 * - **Silence is not evidence.** A child who said nothing, or whose microphone
 *   heard nothing, has told us nothing about their pronunciation. That case
 *   returns no phoneme confidences at all rather than a row of zeroes, because
 *   a zero would drag the gap map down and send them practice they do not need.
 */
import { normalise, editDistance, toleranceFor } from './scoring.js';
/** How many of `target`'s words turn up in `heard`, allowing for slips. */
function overlap(target, heard) {
    const left = [...heard];
    let found = 0;
    for (const word of target) {
        // Nearest first, so a transcript with a near-miss *and* an exact match
        // spends the exact one on the word it belongs to.
        let bestAt = -1;
        let bestDistance = Number.POSITIVE_INFINITY;
        for (const [at, candidate] of left.entries()) {
            const distance = editDistance(word, candidate);
            if (distance < bestDistance) {
                bestDistance = distance;
                bestAt = at;
            }
        }
        if (bestAt >= 0 && bestDistance <= toleranceFor(word)) {
            found += 1;
            left.splice(bestAt, 1);
        }
    }
    return found;
}
const words = (text) => normalise(text).split(' ').filter(Boolean);
/**
 * Score one spoken attempt.
 *
 * Word overlap rather than a phonetic comparison, because a transcript is what
 * there is: the recogniser has already decided what words it heard, and
 * re-deriving sounds from its spelling would be guessing twice. The comparison
 * is deliberately forgiving — `editDistance` is the same one the typed answers
 * use, so `nice` against a transcript of `niece` is a slip rather than a miss,
 * which for a nine-year-old reading English aloud is the right way to be wrong.
 */
export function scoreSpoken(attempt) {
    const heard = words(attempt.heard);
    const quiet = {
        outcome: 'partial',
        score: 0,
        phonemes: {},
        matched: null,
        heardNothing: true,
    };
    if (heard.length === 0)
        return quiet;
    let best = { score: 0, matched: null };
    for (const candidate of attempt.accepted) {
        const target = words(candidate);
        if (target.length === 0)
            continue;
        const score = overlap(target, heard) / target.length;
        if (score > best.score || best.matched === null) {
            best = { score, matched: candidate };
        }
    }
    // Nothing acceptable was authored, so there is nothing to be right about.
    // Silent rather than wrong: the child did speak.
    if (best.matched === null)
        return { ...quiet, heardNothing: false };
    const phonemes = {};
    for (const sound of attempt.targetPhonemes)
        phonemes[sound] = best.score;
    return {
        outcome: best.score >= attempt.tolerance ? 'correct' : 'partial',
        score: best.score,
        phonemes,
        matched: best.matched,
        heardNothing: false,
    };
}
//# sourceMappingURL=speaking.js.map